import { useCallback, useContext, useMemo } from "react";
import { tableHooks } from "components/Table";
import { AssessmentExecutionCell, AssessmentStatusCell, performAssessmentsHooks } from "..";
import { timeAgoWithOutSeconds } from "utils/formatter";
import { Button } from "components/Form";
import { alerts, features, statuses } from 'keys'
import { ReactComponent as RemoveIcon } from 'assets/icons/Remove.svg';
import { ReactComponent as UploadFileIcon } from 'assets/icons/UploadFile.svg'
import { ReactComponent as GoIcon } from 'assets/icons/Go.svg';
import { ReactComponent as PerformAgainIcon } from 'assets/icons/PerformAgain.svg';
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';
import { ReactComponent as PreviewIcon } from 'assets/icons/Preview.svg';
import { actions } from "store";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { app, performAssessments } from "router/routes";
import { AssessmentCategoryCell } from "pages/School/Assessments";
import { updateRedoStatusFetch } from "store/middlewares/markingWords.crud";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient as qc } from "keys";
import { useSelector } from "react-redux";
import { IconButton, SvgIcon, Tooltip } from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { getCurrentSemester } from "utils/general";
import { toast } from "react-toastify";
import { toastConfig } from "config";
import { secretStorage } from "config";
import { TransparentIcon } from "styles/GlobalStyle";
import { PerformAssessmentContext } from "../PerformAssessmentContext";
import { ReactComponent as PlayIcon } from 'assets/icons/Play2.svg';
import { PENDING_STATUSES } from "keys/statuses";
import { permissions } from "config/ability";

const usePerformPopup = () => {
    const { isPractices } = useContext(PerformAssessmentContext)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const openPerformPopup = useCallback((assessmentID, additionalData = {}) => {
        let performRoute = isPractices ? app.performPracticesAssessmentById : app.performAssessmentById
        const routeTo = `${performRoute.replace(':assessmentID', assessmentID)}`
        const hidePerformAloneAlert = localStorage.getItem("hidePerformAloneAlert")
        if (!hidePerformAloneAlert || hidePerformAloneAlert === "false")
            dispatch(actions.setAlerts({
                key: alerts.performAssessment,
                value: {
                    open: true,
                    props: {
                        ...additionalData,
                        test_taker: additionalData.first_name,
                        gender: additionalData.gender,
                        onClose: () => navigate(routeTo, {
                            state: additionalData
                        })
                    }
                }
            }))
        else navigate(routeTo, {
            state: additionalData
        })
    }, [])

    return openPerformPopup
}

const useTestTakerColumns = () => {
    const testTakerNameCell = tableHooks.useTestTakerNameCell()
    const idCell = tableHooks.useIdCell()
    const performAssessments = performAssessmentsHooks.usePerformAssessments()
    const openPerformPopup = usePerformPopup()

    const testTakerColumns = useMemo(
        () => [
            {
                ...idCell,
                accessorKey: "test_taker_id",
                enableSorting: false
            },
            testTakerNameCell,
            {
                accessorKey: "completed_assessment_count",
                header: "מבדקים בוצעו",
                cell: AssessmentExecutionCell,
                enableSorting: false
            },
            {
                accessorKey: 'affiliation_name',
                header: "בית ספר",
                showBrokenDisplay: true,
                enableSorting: false
            },
            {
                accessorKey: 'grade_name',
                header: "שכבה נכחית",
                enableSorting: false
            },
            {
                accessorKey: 'school_class_name',
                header: "כיתה",
            },
            {
                accessorKey: 'perform_assessments',
                header: "ביצוע",
                cell: ({ row }) => {
                    const { assessments_to_perform_ids = [], test_taker_id } = row.original

                    const handleClick = () => {
                        const [firstId, ...rest] = performAssessments(assessments_to_perform_ids)

                        openPerformPopup(firstId, {
                            remainingAssessmentIds: rest,
                            totalAssessmentsCount: assessments_to_perform_ids?.length,
                            ...row.original
                        })
                    }

                    return (
                        <Button
                            label="ביצוע"
                            disabledColor="secondary"
                            action={handleClick}
                            size="small"
                            direction="rtl"
                            icon={<SvgIcon className="play-icon" component={PlayIcon} inheritViewBox />}
                            color="secondary"
                            disabled={!assessments_to_perform_ids.length}
                        />
                    )
                }
            }
        ], []
    )
    return testTakerColumns
}

const useColumns = () => {
    const { brokenDisplay } = useSelector(state => state.general)
    const { setCurrentAssessmentDef, setOpenAssesmentDefDrawer } = useContext(PerformAssessmentContext)

    const openPerformPopup = usePerformPopup()
    const idCell = tableHooks.useIdCell()
    const gradeOfAllocationCell = tableHooks.useGradeOfAllocationCell()
    const playCell = tableHooks.usePlayCell()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const isSuperuser = secretStorage.getItem("user")?.is_superuser


    const { mutateAsync: updateRedoStatus, isPending: updateRedoStatusLoading } = useMutation({
        mutationFn: updateRedoStatusFetch,
        onSuccess: () => {
            queryClient.invalidateQueries([qc.assessments])
        }
    })

    const openUploadAudioPopup = useCallback((id) => {
        dispatch(actions.setAlerts({
            key: alerts.uploadAudio,
            value: { open: true, props: id, }
        }))
    }, [])

    const openRemoveAssessmentPopup = useCallback((id) => {
        dispatch(actions.setAlerts({
            key: alerts.removeAssessment,
            value: { open: true, props: id, }
        }))
    }, [])

    const openPerformAgainPopup = useCallback(({ id }) => {
        dispatch(actions.setAlerts({
            key: alerts.performAssessmentAgain,
            value: {
                open: true,
                props: {
                    onClose: () => updateRedoStatus(id)
                }
            }
        }))
    }, [])

    const columns = useMemo(
        () => [
            {
                ...idCell,
                enableSorting: false
            },
            {
                accessorKey: 'reading_assessment_def_id',
                header: "מבדק",
                feature: features.id_cell,
                role: "read",
                enableSorting: false
            },
            {
                accessorKey: 'reading_assessment_category_label',
                header: "סוג מבדק",
                cell: AssessmentCategoryCell,
                enableSorting: false,
                customComponent: () => null

            },
            {
                accessorKey: 'reading_assessment_title',
                header: "שם המבדק",
                enableSorting: false
            },
            {
                accessorKey: 'status',
                header: "סטטוס",
                cell: AssessmentStatusCell,
                alignCenter: true,
                enableSorting: false,
            },
            {
                ...gradeOfAllocationCell,
                enableSorting: false
            },
            {
                accessorKey: 'reading_assessment_activated_by',
                header: 'בוצע ע"י',
            },
            {
                accessorKey: 'file_uploaded_date',
                header: "מועד ביצוע",
                enableSorting: false,
                cell: ({ row }) => !row.original.file_uploaded_date || timeAgoWithOutSeconds(new Date(row.original.file_uploaded_date))
                // timeAgoWithOutSeconds(
                //     row.original.assessment_group.reduce((latestDate, assessment) => {
                //         const assessmentDate = new Date(assessment.file_uploaded_date);
                //         return assessmentDate > latestDate ? assessmentDate : latestDate;
                //     }, null))
                // || ""
            },

            {
                accessorKey: 'view_assessment_def',
                header: "צפיה",
                enableSorting: false,
                feature: features.view_assessment_def,
                role: "read",
                cell: ({ row }) => {
                    return [
                        <TransparentIcon>
                            <IconButton
                                className="textPrimary"
                                onClick={() => {
                                    setCurrentAssessmentDef(row.original.reading_assessment_def_id)
                                    setOpenAssesmentDefDrawer(true)
                                }}
                            >
                                <SvgIcon component={VisibilityIcon} inheritViewBox />
                            </IconButton>
                        </TransparentIcon>
                    ];
                }
            },
            {
                accessorKey: 'perform_assessment',
                header: "ביצוע",
                enableSorting: false,
                cell: ({ row }) => {
                    const assessment = row.original
                    const enableRow = PENDING_STATUSES.includes(assessment.status)

                    return (
                        <Tooltip
                            title={"לביצוע"}
                            placement="left">
                            <div>
                                <IconButton
                                    className={`transparent-rect`}
                                    disabled={!enableRow}
                                    onClick={() => openPerformPopup(row.original.id, row.original)}
                                >
                                    <SvgIcon className={enableRow ? "" : "disabled"} component={GoIcon} inheritViewBox />
                                </IconButton>
                            </div>
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: 'redo_assessment',
                header: "החזר לביצוע ",
                enableSorting: false,
                feature: features.redo_assessment,
                role: permissions.read,

                cell: ({ row }) => {
                    const assessment = row.original
                    //האפשרות לבצע שוב מבדקים מיועדת רק למבדקים שעוד לא נבדקו
                    const enableRow = assessment.status == statuses.uploading || assessment.status == statuses.uploaded || assessment.status == statuses.missed

                    return (
                        <Tooltip
                            title={"החזר לביצוע "}
                            placement="left">
                            <div>
                                <IconButton
                                    className={`transparent-rect`}
                                    disabled={!enableRow}
                                    onClick={() => openPerformAgainPopup(row.original)}
                                >
                                    <SvgIcon className={enableRow ? "" : "disabled"} component={PerformAgainIcon} inheritViewBox />
                                </IconButton>
                            </div>
                        </Tooltip>
                    );
                }
            },
            playCell,
            {
                accessorKey: 'upload',
                header: "העלאת אודיו",
                enableSorting: false,
                feature: "upload-audio",
                alignCenter: true,
                role: "create",
                cell: ({ row }) => {
                    return (
                        <Tooltip title="להעלאת אודיו" placement="left">
                            <IconButton
                                onClick={() => {
                                    openUploadAudioPopup(row.original.id)
                                }}
                            >
                                <SvgIcon component={UploadFileIcon} inheritViewBox />
                            </IconButton>
                        </Tooltip>
                    )
                }
            },
            {
                accessorKey: 'remove',
                header: "ביטול הקצאה",
                enableSorting: false,
                feature: "upload-audio",
                alignCenter: true,
                role: "create",
                cell: ({ row }) => {
                    const { status, sub_status } = row.original
                    const disabled = status != statuses.created && sub_status

                    return (
                        <Tooltip tooltip="לביטול הקצאה" placement="left">
                            <IconButton
                                disabled={disabled}
                                onClick={() => {
                                    openRemoveAssessmentPopup(row.original.id)
                                }}
                            >
                                <SvgIcon component={RemoveIcon} inheritViewBox />
                            </IconButton>
                        </Tooltip>
                    )
                }
            },
            {
                accessorKey: 'preview',
                header: "הדגמה",
                feature: "preform-assessment-perview",
                role: "read",
                enableSorting: false,
                alignCenter: true,
                cell: ({ row }) => {
                    return (
                        <Tooltip title={"הדגמה - הצצה למה שמחכה לילד באבחון"} placement="left">
                            <IconButton
                                className="transparent-icon preview-icon"
                                onClick={() => {
                                    navigate(`/${performAssessments}/${row.original.id}/preview/`, {})
                                }}
                            >
                                <SvgIcon component={PreviewIcon} inheritViewBox />
                            </IconButton>
                        </Tooltip>
                    )
                }
            }
        ], [brokenDisplay]
    )

    return columns
}

export { useTestTakerColumns, useColumns }